import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PawPrint, Lightning, Crown } from "@phosphor-icons/react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "hoodies", label: "Hoodies" },
  { key: "tees", label: "Tees" },
  { key: "sweatsuits", label: "Sweatsuits" },
  { key: "womens", label: "Mrs. Wigglez" },
  { key: "matching", label: "Matching Sets" },
  { key: "dog-gear", label: "Dog Gear" },
  { key: "accessories", label: "Bandanas" },
  { key: "kids", label: "Kids" },
];

const APPAREL = ["hoodies", "tees", "sweatsuits", "womens", "matching", "dog-gear", "accessories", "kids"];

export default function Shop() {
  const [all, setAll] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(({ data }) => setAll(data)).finally(() => setLoading(false));
  }, []);

  const hero = useMemo(
    () => all.find((p) => p.name.includes("Matching Set — Human + Pit")) || all.find((p) => p.category === "hoodies" && p.featured),
    [all]
  );
  const grail = useMemo(() => all.find((p) => p.name.includes("King Diamond Grail")), [all]);
  const showHero = filter === "all" && !q && !!hero;

  const products = useMemo(() => {
    return all
      .filter((p) => APPAREL.includes(p.category))
      .filter((p) => (filter === "all" ? true : p.category === filter))
      .filter((p) => (q ? p.name.toLowerCase().includes(q.toLowerCase()) : true))
      .filter((p) => !(showHero && hero && p.id === hero.id))
      .filter((p) => !(showHero && grail && p.id === grail.id));
  }, [all, filter, q, hero, grail, showHero]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="shop-page">
      <p className="overline text-primary mb-2">Mr. Wigglez Shop</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-8">The <span className="neon-text">Neon</span> Apparel</h1>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-10">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`overline whitespace-nowrap px-4 py-2 rounded-full border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-muted-foreground hover:text-white"}`}
              data-testid={`shop-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search apparel…"
          className="sm:ml-auto bg-transparent border border-white/15 rounded-full px-5 py-2.5 text-sm outline-none focus:border-primary transition-colors w-full sm:w-64"
          data-testid="shop-search" />
      </div>

      {showHero && (
        <Link to={`/product/${hero.id}`} className="group relative block mb-12 rounded-2xl overflow-hidden border border-primary/40 neon-glow" data-testid="shop-featured-hero">
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden bg-muted">
              <img src={(hero.images || [])[0]} alt={hero.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-4 left-4 overline px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-primary border border-primary/40 inline-flex items-center gap-1.5"><Lightning weight="fill" size={13} /> Featured Drop</span>
            </div>
            <div className="p-8 sm:p-12 flex flex-col justify-center bg-card">
              <span className="overline text-secondary mb-3">The Pack, matched</span>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">{hero.name}</h2>
              <p className="text-muted-foreground mb-7 max-w-md leading-relaxed">{hero.description}</p>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-modern">${hero.price}</span>
                <span className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-[10px] font-modern font-medium group-hover:bg-primary/90 transition-colors">
                  <PawPrint weight="fill" size={16} /> Shop this drop
                </span>
              </div>
            </div>
          </div>
        </Link>
      )}

      {showHero && grail && (
        <Link to={`/product/${grail.id}`} className="group relative block mb-12 rounded-2xl overflow-hidden border border-secondary/40" style={{ boxShadow: "0 0 44px rgba(176,38,255,0.28)" }} data-testid="shop-grail-spotlight">
          <div className="grid md:grid-cols-2">
            <div className="p-8 sm:p-12 flex flex-col justify-center bg-black order-2 md:order-1">
              <span className="overline text-secondary mb-3 inline-flex items-center gap-1.5"><Crown weight="fill" size={14} /> King Diamond · In Memoriam</span>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">{grail.name}</h2>
              <p className="text-muted-foreground mb-7 max-w-md leading-relaxed">{grail.description}</p>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-modern">${grail.price.toLocaleString()}</span>
                <span className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-[10px] font-modern font-semibold group-hover:bg-white/90 transition-colors"><Crown weight="fill" size={16} /> Claim the 1 of 1</span>
              </div>
            </div>
            <div className="relative aspect-[4/5] md:aspect-auto overflow-hidden bg-black order-1 md:order-2">
              <img src={(grail.images || [])[0]} alt={grail.name} className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-4 right-4 overline px-3 py-1.5 rounded-full bg-black/70 backdrop-blur text-white border border-white/30">1 of 1 · VS1</span>
            </div>
          </div>
        </Link>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground py-20 text-center" data-testid="shop-empty">No products found.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shop-grid">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
